// 動態 SVG：依時間軸把元素包上動畫 class，產生 <style> 與遮罩。只用 CSS 與 SMIL，不需要 JavaScript。
// 動畫結束時的畫面等於靜態輸出，不支援動畫的檢視器（例如 resvg）也會看到完整的圖。
import type { Span } from './animation.js';
import { fmt } from './pen.js';

export interface AnimateOptions {
  /** 建構完成後，主幹與第二層連線上持續流動的光點（預設開） */
  flow?: boolean;
  /** 建構動畫總長上限（秒），預設 7 */
  maxDuration?: number;
}

const FLOW_CYCLE = 2.6;
const FLOW_DEPTH_STAGGER = 0.45;
const FLOW_MAX_DEPTH = 2;

const BASE_CSS = `
@keyframes sa-draw{from{stroke-dashoffset:1}to{stroke-dashoffset:0}}
@keyframes sa-pop{0%{opacity:0;transform:scale(.55)}65%{opacity:1;transform:scale(1.07)}100%{opacity:1;transform:scale(1)}}
@keyframes sa-fade{from{opacity:0}to{opacity:1}}
@keyframes sa-march{to{stroke-dashoffset:-28}}
.sa-draw path{stroke-dasharray:1 1;animation-name:sa-draw}
.sa-mask{stroke-dasharray:1 1;animation-name:sa-draw}
.sa-pop{animation-name:sa-pop;transform-box:fill-box;transform-origin:center}
.sa-fade{animation-name:sa-fade}
.sa-draw path,.sa-mask,.sa-pop,.sa-fade{animation-fill-mode:both;animation-timing-function:cubic-bezier(.3,.7,.4,1)}
.sa-march path[stroke-dasharray]{animation:sa-march 1.1s linear infinite}
@media (prefers-reduced-motion:reduce){.sa-draw path,.sa-mask,.sa-pop,.sa-fade,.sa-march path{animation:none!important;stroke-dasharray:none}}`;

export class Animator {
  private readonly classes = new Map<string, string>();
  private readonly masks: string[] = [];
  private readonly flows: string[] = [];

  constructor(private readonly prefix: string, readonly end: number, readonly options: AnimateOptions) {}

  // 每組（開始、持續）時間對應一個 class，輸出時集中成 CSS 規則。
  private timing(span: Span): string {
    const key = `${span.start}|${span.duration}`;
    let name = this.classes.get(key);
    if (!name) { name = `${this.prefix}k${this.classes.size}`; this.classes.set(key, name); }
    return name;
  }

  /** 描邊動畫：所有 path 加 pathLength="1"，從頭畫到尾 */
  draw(markup: string, span: Span | undefined): string {
    if (!span || !markup) return markup;
    return `<g class="sa-draw ${this.timing(span)}">${markup.replace(/<path /g, '<path pathLength="1" ')}</g>`;
  }

  /** 彈出：縮放加淡入 */
  pop(markup: string, span: Span | undefined): string {
    if (!span || !markup) return markup;
    return `<g class="sa-pop ${this.timing(span)}">${markup}</g>`;
  }

  /** 淡入；march 為 true 時虛線持續流動（關聯線） */
  fade(markup: string, span: Span | undefined, march = false): string {
    if (!span || !markup) return markup;
    return `<g class="sa-fade ${this.timing(span)}${march ? ' sa-march' : ''}">${markup}</g>`;
  }

  /** 填色的形狀（錐形主幹）：以沿中心線描邊的遮罩逐步顯示 */
  reveal(markup: string, centerline: string, width: number, span: Span | undefined): string {
    if (!span || !markup) return markup;
    const id = `${this.prefix}m${this.masks.length}`;
    // 遮罩範圍要明確給大：預設的 -10%～120% 是以畫布計算，內容平移後會被裁掉。
    this.masks.push(`<mask id="${id}" maskUnits="userSpaceOnUse" x="-50000" y="-50000" width="100000" height="100000"><path class="sa-mask ${this.timing(span)}" d="${centerline}" pathLength="1" fill="none" stroke="#fff" stroke-width="${fmt(width * 2 + 18)}" stroke-linecap="round"/></mask>`);
    return `<g mask="url(#${id})">${markup}</g>`;
  }

  /** 建構完成後沿連線流動的光點（SMIL），依深度錯開形成往外傳遞的效果 */
  flow(path: string, color: string, depth: number): void {
    if (this.options.flow === false || depth > FLOW_MAX_DEPTH) return;
    const begin = fmt(this.end + 0.3 + (depth - 1) * FLOW_DEPTH_STAGGER);
    const timing = `dur="${FLOW_CYCLE}s" begin="${begin}s" repeatCount="indefinite"`;
    this.flows.push(`<g opacity="0"><circle r="7" fill="${color}" opacity=".28"/><circle r="3.2" fill="${color}"/>`
      + `<animateMotion path="${path}" ${timing} keyPoints="0;1;1" keyTimes="0;.45;1" calcMode="linear"/>`
      + `<animate attributeName="opacity" values="0;1;1;0;0" keyTimes="0;.06;.38;.45;1" ${timing}/></g>`);
  }

  /** 放在內容最上層的光點 */
  overlay(): string {
    return this.flows.join('');
  }

  defs(): string {
    return this.masks.join('');
  }

  style(): string {
    const rules = [...this.classes].map(([key, name]) => {
      const [start, duration] = key.split('|');
      return `.${name},.${name} path{animation-delay:${start}s;animation-duration:${duration}s}`;
    });
    return `<style>${BASE_CSS}\n${rules.join('\n')}</style>`;
  }
}
